/***************************************************************************/
"use client";
/***************************************************************************/

import { cva, VariantProps } from "class-variance-authority";
import debounce from "lodash.debounce";
import * as React from "react";
import { cn } from "../../utils/css";

const inputVariants = cva(
  [
    "min-w-60 w-full whitespace-nowrap transition-colors",
    "flex items-center justify-between",
    "box-border border",
    "shadow-sm",
    "bg-transparent placeholder:text-neutral-500 data-[placeholder]:text-neutral-500 [&>.--placeholder]:text-neutral-500",
    "ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring focus-visible:ring-offset-background [&>span]:line-clamp-1",
    "file:border-0 file:bg-transparent file:text-sm file:font-medium",
    "disabled:cursor-not-allowed disabled:opacity-50",
  ].join(" "),
  {
    variants: {
      variant: {
        default:
          "text-foreground border-input bg-background hover:bg-neutral-50",
        primary:
          "border-primary/50 text-primary hover:bg-primary-50/80 placeholder:text-primary/50 data-[placeholder]:text-primary/50 [&>.--placeholder]:text-primary/50",
        success:
          "border-success/50 text-success hover:bg-success-50/80 placeholder:text-success/50 data-[placeholder]:text-success/50 [&>.--placeholder]:text-success/50",
        info: "border-info/50 text-info hover:bg-info-50/80 placeholder:text-info/50 data-[placeholder]:text-info/50 [&>.--placeholder]:text-info/50",
        warning:
          "border-warning/50 text-warning hover:bg-warning-50/80 placeholder:text-warning/50 data-[placeholder]:text-warning/50 [&>.--placeholder]:text-warning/50",
        error:
          "border-error/50 text-error hover:bg-error-50/80 placeholder:text-error/50 data-[placeholder]:text-error/50 [&>.--placeholder]:text-error/50",
        secondary:
          "border-gray-100 bg-gray-100 hover:border-gray-200 hover:bg-gray-200/80",
      },
      size: {
        default: "min-h-10 rounded px-3 py-1.5 gap-2",
        sm: "min-h-8 rounded px-2 py-1 text-xs gap-1",
        lg: "min-h-11 rounded-md px-4 py-1.5 gap-2",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

const inputIconVariants = cva("[&_.lucide]:text-neutral-500", {
  variants: {
    variant: {
      default: "[&_.lucide]:text-neutral-500",
      primary: "[&_.lucide]:text-primary/60",
      success: "[&_.lucide]:text-success/60",
      info: "[&_.lucide]:text-info/60",
      warning: "[&_.lucide]:text-warning/60",
      error: "[&_.lucide]:text-error/60",
      secondary:
        "border-gray-100 bg-gray-100 hover:border-gray-200 hover:bg-gray-200/80",
    },
    size: {
      default: "[&_.lucide]:h-4 [&_.lucide]:w-4",
      sm: "[&_.lucide]:h-3 [&_.lucide]:w-3",
      lg: "[&_.lucide]:h-5 [&_.lucide]:w-5",
    },
  },
  defaultVariants: {
    size: "default",
  },
});

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size">,
    VariantProps<typeof inputVariants> {
  disableSpinner?: boolean;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, variant, size, disableSpinner, ...props }, ref) => {
    const spinnerClass =
      type === "number" && disableSpinner
        ? "appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [-moz-appearance:textfield]"
        : "";

    return (
      <input
        type={type}
        className={cn(
          inputVariants({ variant, size }),
          className,
          spinnerClass,
        )}
        ref={ref}
        {...props}
      />
    );
  },
);
Input.displayName = "Input";

const InputIcon: React.FC<
  React.HTMLAttributes<HTMLSpanElement> & VariantProps<typeof inputIconVariants>
> = ({ className, variant, size, ...props }) => {
  return (
    <span
      className={cn(inputIconVariants({ size, variant }), className)}
      {...props}
    />
  );
};

const SearchInput = React.forwardRef<
  HTMLInputElement,
  InputProps & { debounceWait?: number }
>(({ className, value, debounceWait = 500, onChange, ...props }, ref) => {
  const [inputValue, setInputValue] = React.useState(value);

  const onDebouncedChange = React.useCallback(
    debounce((e: React.ChangeEvent<HTMLInputElement>) => {
      if (onChange) {
        onChange(e);
      }
    }, debounceWait),
    [],
  );

  const onInputValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    onDebouncedChange(e);
  };

  React.useEffect(() => {
    setInputValue(value);
  }, [value]);

  return (
    <Input
      ref={ref}
      {...props}
      className={cn("--search-input", className)}
      value={inputValue ?? ""}
      onChange={onInputValueChange}
    />
  );
});
SearchInput.displayName = "SearchInput";

export { Input, InputIcon, inputIconVariants, inputVariants, SearchInput };
